import { useEffect, useState } from 'react';
import { startOfDay } from 'date-fns';
import ChooseAvailableTime from '../components/ChooseAvailableTime';
import SeriesPicker from './SeriesPicker';
import Modal from '~/components/Modal';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { isAdminApp } from '~/public/lib/PageHelpers.js';
import RecurringModal from '~/components/forms/questions/scheduling/RecurringModal';
import {
    clientFormatDateLong,
    clientFormatDateTimeLong,
} from '~/public/lib/ClientDateTime.js';

const NextTimesOverlay = ({
    onRecur,
    onAddAnother,
    onContinue,
    onCancel,
    time,
    allTimes,
    settings,
    hideTimes,
    clientTz,
}) => {
    const numMoreTimes = allTimes.length - 1;
    const isAdmin = isAdminApp();

    return (
        <div className="absolute rounded-2xl overflow-hidden z-10 bg-[#FFFFFF00] h-full w-full backdrop-blur flex flex-col items-center justify-center">
            <div className="font-semibold mb-5 leading-tight">
                {hideTimes
                    ? clientFormatDateLong(time, clientTz)
                    : clientFormatDateTimeLong(time, clientTz)}

                {numMoreTimes > 0 && (
                    <div className="font-normal text-center text-gray-500">
                        +{numMoreTimes} more time{numMoreTimes > 1 ? 's' : ''}
                    </div>
                )}
            </div>
            <button
                type="button"
                className={`${isAdmin ? 'btn-pill' : 'form-button'} mb-3`}
                onClick={onContinue}
            >
                Continue
            </button>
            <button
                type="button"
                className={`${isAdmin ? 'btn-light-pill' : 'form-button-light'} mb-3`}
                onClick={onAddAnother}
            >
                Add More Times
            </button>
            <button
                type="button"
                className={`${isAdmin ? 'btn-light-pill' : 'form-button-light'} mb-3`}
                onClick={onRecur}
            >
                Make Recurring
            </button>
            <button
                type="button"
                className={`${isAdmin ? 'btn-inverse-pill' : 'link-no-underline'} mb-3`}
                onClick={onCancel}
            >
                Cancel
            </button>
        </div>
    );
};

const SelectedTimeList = ({
    times,
    className,
    onChange,
    hideTimes,
    disableRemove,
    showContinueButton,
    onContinue,
    clientTz,
}) => {
    if (times.length === 0 || (disableRemove && times.length === 1)) {
        return null;
    }

    return (
        <div className={className}>
            <div className="panel-label">Selected Times</div>
            <div className="bg-white border border-gray-300 rounded-lg pl-6">
                {times.map((time, idx) => (
                    <div
                        className={`pr-6 py-6 flex items-center justify-between ${idx > 0 ? 'border-t' : ''}`}
                    >
                        <div>
                            {hideTimes
                                ? clientFormatDateLong(time, clientTz)
                                : clientFormatDateTimeLong(time, clientTz)}
                        </div>
                        {!disableRemove && (
                            <button
                                type="button"
                                className="text-red-600 w-5 h-5"
                                onClick={() => {
                                    let newTimes = [...times];
                                    newTimes.splice(idx, 1);
                                    onChange(newTimes);
                                }}
                            >
                                <XMarkIcon />
                            </button>
                        )}
                    </div>
                ))}
            </div>
            {!!showContinueButton && (
                <button
                    type="button"
                    className={`${isAdminApp() ? 'btn-pill' : 'form-button'} mt-2`}
                    onClick={onContinue}
                >
                    Continue
                </button>
            )}
        </div>
    );
};

const PickStartTime = ({
    value,
    onChange,
    settings,
    eventType,
    quantity,
    bookableResourceId,
    clientTz,
    initialSelectedDate,
    extraTime,
}) => {
    const [lastDate, setLastDate] = useState(initialSelectedDate);
    const [isAddingState, setIsAddingState] = useState(false);
    const [hideFutureOverlays, setHideFutureOverlays] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [showRecurring, setShowRecurring] = useState(false);
    const [lastTimePicked, setLastTimePicked] = useState(null);

    if (eventType?.is_all_day) {
        // For all day events you don't need to pick a specific time
        settings = { ...settings, hideStartTimes: true };
    }

    useEffect(() => {
        if (value) {
            setLastDate(startOfDay(new Date(value)));
        }
    }, [value]);

    if (eventType?.is_series) {
        return (
            <SeriesPicker
                value={value}
                onChange={onChange}
                settings={settings}
                eventType={eventType}
                bookableResourceId={bookableResourceId}
                clientTz={clientTz}
                quantity={quantity}
            />
        );
    }

    let isAddingTimes = !value;
    if (settings?.enableRecurring && !isAddingTimes) {
        // If we're recurring we can also be editing
        isAddingTimes = isAddingState;
    }
    const allTimes = (Array.isArray(value) ? value : [value]).filter(v => !!v);

    const hideTimes = !!settings?.hideStartTimes;

    return (
        <div>
            <div className="mt-4 input-label">Date &amp; Time</div>
            {!isAddingTimes && (
                <section
                    className="choose-block choose-block-selected"
                    onClick={() => onChange()}
                >
                    <div className="flex-1 p-6">
                        {allTimes.map(t => (
                            <p>
                                {hideTimes
                                    ? clientFormatDateLong(t, clientTz)
                                    : clientFormatDateTimeLong(t, clientTz)}
                            </p>
                        ))}
                    </div>
                    <div>
                        <button
                            type="button"
                            className="mr-6 btn-inverse btn-small text-sm btn-light-pill-sm uppercase"
                        >
                            Edit
                        </button>
                    </div>
                </section>
            )}
            {isAddingTimes && (
                <>
                    <div className="relative">
                        {showOverlay && (
                            <NextTimesOverlay
                                settings={settings}
                                time={lastTimePicked}
                                allTimes={allTimes}
                                hideTimes={hideTimes}
                                clientTz={clientTz}
                                onCancel={() => {
                                    // Remove last time picked
                                    onChange(
                                        allTimes.filter(
                                            t => t !== lastTimePicked,
                                        ),
                                    );
                                    setIsAddingState(true);
                                    setShowOverlay(false);
                                }}
                                onRecur={() => {
                                    setIsAddingState(true);
                                    setShowRecurring(true);
                                    setShowOverlay(false);
                                }}
                                onAddAnother={() => {
                                    setIsAddingState(true);
                                    // We'll default to 'add another time' going forward
                                    //  because it would be weird to add another THEN recurring
                                    setHideFutureOverlays(true);
                                    setShowOverlay(false);
                                }}
                                onContinue={() => {
                                    setIsAddingState(false);
                                    setShowOverlay(false);
                                }}
                            />
                        )}
                        <RecurringModal
                            isOpen={showRecurring}
                            onClose={() => {
                                setShowRecurring(false);
                                setIsAddingState(false);
                            }}
                            time={lastTimePicked}
                            value={value}
                            hideTimes={hideTimes}
                            onChange={onChange}
                            eventType={eventType}
                            bookableResourceId={bookableResourceId}
                            allowCustomTime={!!settings?.allowCustomTime}
                        />
                        <ChooseAvailableTime
                            value={value}
                            date={settings?.hideStartTimes ? null : lastDate}
                            onChange={(newValue, isCustom) => {
                                if (settings?.enableRecurring) {
                                    // Are we adding this?
                                    setIsAddingState(true);
                                    setShowOverlay(!hideFutureOverlays);
                                    setLastTimePicked(newValue);

                                    const currentValue = value || [];
                                    if (
                                        hideFutureOverlays &&
                                        currentValue.includes(newValue)
                                    ) {
                                        // We're in adding more times mode and already selected this
                                        //  so remove it!
                                        const v = [...currentValue];
                                        v.splice(
                                            currentValue.indexOf(newValue),
                                            1,
                                        );
                                        onChange(v);
                                    } else {
                                        // Add it
                                        onChange([...currentValue, newValue]);
                                    }
                                } else {
                                    onChange(newValue);
                                }
                            }}
                            currentSelectedTimes={allTimes}
                            clientTz={clientTz}
                            eventTypeId={eventType?.id}
                            isClass={!!eventType?.is_class}
                            bookableResourceId={bookableResourceId}
                            settings={settings}
                            quantity={quantity}
                            extraTime={extraTime}
                        />
                    </div>
                    <SelectedTimeList
                        times={allTimes}
                        hideTimes={hideTimes}
                        onChange={onChange}
                        disableRemove={showOverlay}
                        showContinueButton={hideFutureOverlays}
                        onContinue={() => setIsAddingState(false)}
                        className="mt-4"
                        clientTz={clientTz}
                    />
                </>
            )}
        </div>
    );
};

export default PickStartTime;
